import { useState, useEffect, useCallback, useMemo } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import { motion } from "framer-motion";
import { User, MessageSquare, Clock, RefreshCw, Search } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const messageAnimation = { initial: { opacity: 0, y: 15 }, animate: { opacity: 1, y: 0 } };

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("es-ES", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function AdminLogin({ onAuthenticated }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem("medboard_admin", "true");
      onAuthenticated();
    } catch {
      setError("Contraseña incorrecta");
    }
    setLoading(false);
  };

  return (
    <main data-testid="admin-login-page" className="pt-28 pb-24 bg-[#0a0f1f] min-h-screen">
      <div className="max-w-md mx-auto px-6">
        <h1 className="font-playfair text-4xl text-[#f8f9fa] tracking-tight mb-8 text-center">
          Panel de Administración
        </h1>
        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <label className="font-outfit text-sm text-[#94a3b8] mb-2 block">Contraseña</label>
            <input
              type="password"
              data-testid="admin-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-[#131b2f] border border-[#2a3449] rounded-xl px-5 py-3.5 font-outfit text-[#f8f9fa] placeholder-[#64748b] focus:outline-none focus:border-[#1a6cf5]/50 focus:ring-1 focus:ring-[#1a6cf5]/20 transition-all"
              placeholder="Introduce la contraseña"
            />
          </div>
          {error && <p className="text-red-400 text-sm font-outfit">{error}</p>}
          <button
            type="submit"
            data-testid="admin-login-btn"
            disabled={loading}
            className="w-full bg-[#1a6cf5] hover:bg-[#3b82f6] disabled:opacity-50 text-white font-outfit px-8 py-3.5 rounded-full transition-all duration-300"
          >
            {loading ? "Accediendo..." : "Acceder"}
          </button>
        </form>
      </div>
    </main>
  );
}

function MessageCard({ msg, index }) {
  return (
    <motion.div
      key={msg.id}
      {...messageAnimation}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      data-testid={`admin-message-${index}`}
      className="bg-[#131b2f] border border-[#2a3449] rounded-xl p-6 hover:border-[#1a6cf5]/30 transition-all"
    >
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
        <div className="flex-1 space-y-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#1a6cf5]/10 flex items-center justify-center shrink-0">
              <User size={18} className="text-[#1a6cf5]" />
            </div>
            <div>
              <p className="font-outfit text-[#f8f9fa] font-medium">{msg.name}</p>
              <a href={`mailto:${msg.email}`} className="font-outfit text-xs text-[#1a6cf5] hover:underline">
                {msg.email}
              </a>
            </div>
          </div>
          <p className="font-outfit text-sm text-[#94a3b8] leading-relaxed pl-[52px]">{msg.message}</p>
        </div>
        <div className="flex items-center gap-2 text-[#64748b] shrink-0 pl-[52px] md:pl-0">
          <Clock size={12} />
          <span className="font-outfit text-xs">{formatDate(msg.created_at)}</span>
        </div>
      </div>
    </motion.div>
  );
}

function MessageList({ messages, loading, search }) {
  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#1a6cf5] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="text-center py-20">
        <MessageSquare size={40} className="mx-auto text-[#2a3449] mb-4" />
        <p className="font-outfit text-[#64748b]">
          {search ? "No se encontraron resultados" : "No hay mensajes aún"}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {messages.map((msg, i) => (
        <MessageCard key={msg.id} msg={msg} index={i} />
      ))}
    </div>
  );
}

export default function Admin() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [authenticated, setAuthenticated] = useState(
    () => sessionStorage.getItem("medboard_admin") === "true"
  );

  const [fetchError, setFetchError] = useState(false);

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    setFetchError(false);
    try {
      const res = await axios.get(`${API}/contacts`);
      setMessages(res.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
    } catch (error) {
      setFetchError(true);
      // Error captured in fetchError state for UI feedback
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (authenticated) fetchMessages();
  }, [authenticated, fetchMessages]);

  const filtered = useMemo(
    () =>
      messages.filter(
        (m) =>
          m.name.toLowerCase().includes(search.toLowerCase()) ||
          m.email.toLowerCase().includes(search.toLowerCase()) ||
          m.message.toLowerCase().includes(search.toLowerCase())
      ),
    [messages, search]
  );

  if (!authenticated) {
    return <AdminLogin onAuthenticated={() => setAuthenticated(true)} />;
  }

  return (
    <main data-testid="admin-page" className="pt-28 pb-24 bg-[#0a0f1f] min-h-screen">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-10 gap-4">
          <div>
            <h1 className="font-playfair text-4xl md:text-5xl text-[#f8f9fa] tracking-tight">
              Mensajes de Contacto
            </h1>
            <p className="font-outfit text-sm text-[#64748b] mt-2">
              {messages.length} mensaje{messages.length !== 1 ? "s" : ""} recibido{messages.length !== 1 ? "s" : ""}
            </p>
          </div>
          <button
            onClick={fetchMessages}
            data-testid="admin-refresh-btn"
            className="inline-flex items-center gap-2 bg-[#2a3449] hover:bg-[#3a465f] text-white font-outfit text-sm px-5 py-2.5 rounded-full transition-all"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            Actualizar
          </button>
        </div>

        <div className="relative mb-8">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#64748b]" />
          <input
            type="text"
            data-testid="admin-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre, email o mensaje..."
            className="w-full bg-[#131b2f] border border-[#2a3449] rounded-xl pl-11 pr-5 py-3 font-outfit text-sm text-[#f8f9fa] placeholder-[#64748b] focus:outline-none focus:border-[#1a6cf5]/50 transition-all"
          />
        </div>

        <MessageList messages={filtered} loading={loading} search={search} />
        {fetchError && (
          <p className="font-outfit text-sm text-red-400/70 text-center mt-4">
            Error al cargar mensajes. Intenta actualizar.
          </p>
        )}
      </div>
    </main>
  );
}
